import type { EmailIntegration, EmailMessageDraft, User } from "@/schema"

export interface SendableAddress {
  email: string
  displayName?: string
  integrationId: string
  integrationLabel?: string
  isPrimaryIntegration?: boolean
}

function isSendableAddress(addr: {
  treat_as_alias?: boolean | null
  verification_status?: "accepted" | "pending" | null
}): boolean {
  if (addr.treat_as_alias === false) return false
  if (addr.verification_status === "pending") return false
  return true
}

export function buildSendableAddresses(
  integrations: EmailIntegration[],
  includeConnectedAddress = false
): SendableAddress[] {
  return integrations.flatMap((integration) => {
    const sendableAddresses = integration.sending_addresses?.filter(isSendableAddress) ?? []

    const addresses: SendableAddress[] = sendableAddresses.length
      ? sendableAddresses.map((addr) => ({
          email: addr.email,
          displayName: addr.display_name || undefined,
          integrationId: integration.id,
          integrationLabel: `${integration.user.full_name} · ${integration.connected_email}`,
          isPrimaryIntegration: integration.is_primary,
        }))
      : [
          {
            email: integration.connected_email,
            integrationId: integration.id,
            integrationLabel: `${integration.user.full_name} · ${integration.connected_email}`,
            isPrimaryIntegration: integration.is_primary,
          },
        ]

    if (
      includeConnectedAddress &&
      integration.connected_email &&
      !addresses.some((address) => address.email.toLowerCase() === integration.connected_email.toLowerCase())
    ) {
      addresses.push({
        email: integration.connected_email,
        integrationId: integration.id,
        integrationLabel: `${integration.user.full_name} · ${integration.connected_email}`,
        isPrimaryIntegration: integration.is_primary,
      })
    }
    return addresses
  })
}

export function getDefaultAddress(integrations: EmailIntegration[]): SendableAddress | undefined {
  const defaultIntegration = integrations[0]
  if (!defaultIntegration) return undefined

  const sendableAddresses = defaultIntegration.sending_addresses?.filter(isSendableAddress) ?? []
  const defaultAddr = sendableAddresses.find((addr) => addr.is_default) ?? sendableAddresses[0]

  if (defaultAddr) {
    return {
      email: defaultAddr.email,
      displayName: defaultAddr.display_name || undefined,
      integrationId: defaultIntegration.id,
      isPrimaryIntegration: defaultIntegration.is_primary,
    }
  }

  return {
    email: defaultIntegration.connected_email,
    integrationId: defaultIntegration.id,
    isPrimaryIntegration: defaultIntegration.is_primary,
  }
}

// Existing drafts and per-message choices take precedence over a personal preference.
export function resolveCompositionSender({
  selectedAddress,
  draft,
  integrations,
  user,
  ready,
}: {
  selectedAddress?: SendableAddress | null
  draft?: EmailMessageDraft
  integrations: EmailIntegration[]
  user?: Pick<User, "default_sender_integration_id" | "default_sender_address">
  ready: boolean
}): { address?: SendableAddress; unavailable: boolean } {
  if (selectedAddress) return { address: selectedAddress, unavailable: false }
  if (!ready) return { unavailable: false }

  const addresses = buildSendableAddresses(integrations, true)
  if (draft?.from?.email) {
    const address = addresses.find(
      (candidate) =>
        candidate.email.toLowerCase() === draft.from?.email.toLowerCase() &&
        (!draft.email_integration_id || candidate.integrationId === draft.email_integration_id)
    )
    return {
      address:
        address ??
        (draft.email_integration_id
          ? {
              email: draft.from.email,
              integrationId: draft.email_integration_id,
            }
          : undefined),
      unavailable: false,
    }
  }

  if (draft?.email_integration_id) {
    return {
      address: getDefaultAddress(integrations.filter((integration) => integration.id === draft.email_integration_id)),
      unavailable: false,
    }
  }

  // Replies and forwards retain their existing sender selection.
  if (draft?.replyToSyncboxMessageId || draft?.outbound_reply_to_id || draft?.isForward) {
    return { address: getDefaultAddress(integrations), unavailable: false }
  }

  const hasPreference = !!user?.default_sender_integration_id && !!user.default_sender_address
  if (!hasPreference) return { address: getDefaultAddress(integrations), unavailable: false }

  const availableIntegrations = integrations.filter((integration) => !integration.needs_reauth)
  const preferred = addresses.find(
    (address) =>
      address.integrationId === user.default_sender_integration_id &&
      address.email.toLowerCase() === user.default_sender_address?.toLowerCase() &&
      !integrations.find((integration) => integration.id === address.integrationId)?.needs_reauth
  )
  return { address: preferred ?? getDefaultAddress(availableIntegrations), unavailable: !preferred }
}
